import { useEffect, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router-dom"
import { sub, startOfMonth, format } from "date-fns"
import { rootStore } from "stores/root-store"
import { ContentWrapper, MainComponent } from "components/common-styles"
import { homeBusiness } from "businesses/home-business"
import { TopbarPortal, TOPBAR_TITLE_SLOT_ID } from "components/layout/topbar-portal"
import { SegmentedPeriod } from "components/layout/segmented-period"
import MarketingBanner from "components/home/banner"
import { PendingReceiptsWidget, ExceptionsWidget, LowestStockWidget, OrderStatusWidget, OrdersWidget, SlowMovingWidget, UnitsWidget, ClosedReceiptsWidget } from "components/home/widgets"
import { trackEvent } from "utils/analytics"

const C = {
  text: "#121828",
  muted: "#65748B",
  brand: "#F3DA00",
  surfaceMuted: "#F9FAFC",
}

const greetingForHour = () => {
  const h = new Date().getHours()
  if (h < 12) return "Good morning"
  if (h < 17) return "Good afternoon"
  return "Good evening"
}

const HOME_PERIODS = [
  { key: "today", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "month", label: "Month" },
]

// Maps a dashboard widget's display value to the value the Sales Order search
// expects for its "order_status" filter. The widget labels ("Processing",
// "Exceptions") differ from the search filter values ("In Process",
// "Exception"), so the translation lives here.
const STATUS_TO_ORDER_STATUS = {
  "In Pool": "In Pool",
  Processing: "In Process",
  "On Hold": "On Hold",
  Exceptions: "Exception",
}

// A "Despatched" order is one that has been picked, packed and despatched — i.e.
// its status is Closed.
const DESPATCHED_ORDER_STATUSES = ["Closed"]

// The "Created" KPI counts every order created, regardless of where it now sits,
// so it maps to all order statuses.
const ALL_ORDER_STATUSES = ["In Pool", "On Hold", "In Process", "Completed", "Closed", "Exception"]

// The Created / Despatched KPIs are scoped to the selected dashboard period, so
// their links carry a matching date range. The search filters by date only (no
// time), so "24h" maps to today — the closest the search can express.
const periodDateRange = period => {
  const end = new Date()
  let start
  switch (period) {
    case "7d":
      start = sub(end, { days: 7 })
      break
    case "30d":
      start = sub(end, { days: 30 })
      break
    case "month":
      start = startOfMonth(end)
      break
    case "today":
    default:
      start = end
  }
  // The search's date filter treats before_date as the start (older) bound and
  // after_date as the end (newer) bound.
  return { before_date: format(start, "yyyy-MM-dd"), after_date: format(end, "yyyy-MM-dd") }
}

// Maps an Exceptions widget reason to the search "exception_type" filter value.
const REASON_TO_EXCEPTION_TYPE = {
  "No/Ineligible Carrier Error": "NO_CARRIER_INELIGIBLE",
  "On Hold": "ON_HOLD",
  "Over-ordered": "OVER_ORDERED",
  "Address Validation Error": "ADDR_VALIDATION_ERROR",
}

// One-page viewport fit is applied ONLY when the screen is big enough to hold
// the full dashboard — desktop width AND height. On shorter screens (e.g. a
// scaled 14" laptop) these rules fall away entirely: rows take their natural
// content height and the canvas scrolls, so tiles are never clipped.
const FIT = "@media (min-width:1200px) and (min-height:950px)"

// Keep the dashboard's three-card rows together on tablets. The cards can
// shrink within the available width, while narrow phones still use one
// column instead of producing orphaned cards on their own rows.
const rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
  // grid rows get equal share of the row's height so cards stay aligned
  // whenever multiple visual rows form (2-col wraps).
  gridAutoRows: "1fr",
  gap: 1.5,
  // In fit mode rows share the viewport height. Their min-height deliberately
  // stays at the default (`auto` = content size) so a row can never be
  // squeezed below its content — if space runs out, the page scrolls instead
  // of clipping tiles.
  [FIT]: { flex: 1 },
}

// KPI row fills the full width like the others but takes a smaller share
// of the available vertical space — the stats are content-light so the
// content-heavy rows below benefit from the extra height.
const kpiRowSx = {
  ...rowSx,
  [FIT]: { flex: 0.55 },
}

const EMPTY_HOME_DATA = {
  periodLabel: "Last 24 hours",
  ordersCreated: 0,
  ordersDespatched: 0,
  unitsCreated: 0,
  unitsDespatched: 0,
  closedReceipts: 0,
  inboundUnits: 0,
  pendingReceipts: [],
  orderStatus: { "In Pool": 0, Processing: 0, "On Hold": 0, Exceptions: 0 },
  exceptionReasons: [
    { reason: "No/Ineligible Carrier Error", count: 0 },
    { reason: "On Hold", count: 0 },
    { reason: "Over-ordered", count: 0 },
    { reason: "Address Validation Error", count: 0 },
  ],
  slowMoving: [],
  lowestStock: [],
}

const Home = observer(() => {
  const navigate = useNavigate()
  const [period, setPeriod] = useState("today")
  const [data, setData] = useState(EMPTY_HOME_DATA)
  const [loading, setLoading] = useState({ orders: true, receipts: true, slowMoving: true, lowestStock: true })

  // Navigate to a search page with the given filters pre-applied. The search
  // page reads these from the query string, seeds the filters and runs the
  // search, so the user lands directly on the matching result list.
  // tile: which dashboard tile was followed, for analytics
  const goToSearch = (path, filters, tile) => {
    trackEvent("dashboard_tile_click", { tile })
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, val]) => {
      const value = Array.isArray(val) ? val.join(",") : val
      if (value) params.set(key, value)
    })
    navigate(`${path}?${params.toString()}`)
  }

  const goToOrderSearch = (filters, tile) => goToSearch("/sales-order", filters, tile)
  const goToInboundSearch = (filters, tile) => goToSearch("/inbound-delivery", filters, tile)
  const goToProductSearch = (filters, tile) => goToSearch("/products", filters, tile)

  const handleStatusClick = label => {
    const status = STATUS_TO_ORDER_STATUS[label]
    if (status) goToOrderSearch({ order_status: [status] }, "order_status")
  }

  const handleExceptionReasonClick = reason => {
    const exceptionType = REASON_TO_EXCEPTION_TYPE[reason]
    if (exceptionType) goToOrderSearch({ exception_type: [exceptionType] }, "exception_reason")
  }

  const handleAllExceptionsClick = () => goToOrderSearch({ order_status: ["Exception"] }, "orders_exception")

  const handleCreatedClick = () => goToOrderSearch({ order_status: ALL_ORDER_STATUSES, ...periodDateRange(period) }, "orders_created")

  const handleDespatchedClick = () => goToOrderSearch({ order_status: DESPATCHED_ORDER_STATUSES, ...periodDateRange(period) }, "orders_despatched")

  // Closed receipts are period-scoped on the dashboard, so carry the period
  // through as a date range; pending receipts are current (no date filter).
  const handleClosedReceiptsClick = () => goToInboundSearch({ receipt_status: ["Closed"], ...periodDateRange(period) }, "receipts_closed")

  const handlePendingReceiptsClick = () => goToInboundSearch({ receipt_status: ["Open"] }, "receipts_pending")

  const handlePendingReceiptRowClick = asn => {
    if (asn && asn !== "-") goToInboundSearch({ search_term: asn }, "receipts_pending_row")
  }

  const handleProductClick = (sku, tile) => {
    if (sku && sku !== "-") goToProductSearch({ search_term: sku }, tile)
  }

  useEffect(() => {
    // Home shows its greeting in the top bar (portalled below), so clear the
    // plain screen title.
    rootStore.setScreenTitle("")
    trackEvent("dashboard_view")
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadDashboard = async () => {
      setLoading({ orders: true, receipts: true, slowMoving: true, lowestStock: true })

      const updateSource = (source, patch) => {
        if (cancelled) return
        setData(current => ({ ...current, ...patch }))
        setLoading(current => ({ ...current, [source]: false }))
      }

      homeBusiness.getOrdersData(rootStore.company, period).then(orders => updateSource("orders", orders))
      homeBusiness.getReceiptsData(rootStore.company, period).then(receipts => updateSource("receipts", receipts))
      homeBusiness.getSlowMovingData(rootStore.company).then(slowMoving => updateSource("slowMoving", { slowMoving }))
      homeBusiness.getLowestStockData(rootStore.company).then(lowestStock => updateSource("lowestStock", { lowestStock }))
    }

    loadDashboard()
    return () => {
      cancelled = true
    }
  }, [period, rootStore.company])

  const firstName = (rootStore.userName || "").split(" ")[0]

  return (
    <MainComponent
      component="main"
      sx={{
        bgcolor: C.surfaceMuted,
        display: "flex",
        position: "relative",
        // Fit the rounded canvas exactly when the screen is big enough;
        // overflow auto (not hidden) is the belt-and-braces — if the row
        // content floors ever exceed the viewport, it scrolls, never clips.
        [FIT]: { height: "100%", overflow: "auto" },
      }}
    >
      <ContentWrapper
        maxWidth={false}
        sx={{
          display: "flex",
          flexDirection: "column",
          [FIT]: { height: "100%" },
        }}
      >
        <Stack spacing={1.5} sx={{ py: 1.5, [FIT]: { height: "100%", minHeight: 0 } }}>
          {/* Greeting — rendered into the top bar's title slot */}
          <TopbarPortal slot={TOPBAR_TITLE_SLOT_ID}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -0.3, whiteSpace: "nowrap" }}>
                  {greetingForHour()}
                  {firstName ? `, ${firstName}` : ""}
                </Typography>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: C.brand, alignSelf: "center" }} />
              </Stack>
              <Typography variant="caption" sx={{ color: C.muted, whiteSpace: "nowrap", display: { xs: "none", xl: "block" } }}>
                {rootStore.company ? `Snapshot for ${rootStore.company}` : "Select a customer to see their snapshot"}
              </Typography>
            </Stack>
          </TopbarPortal>

          {/* Time filter — rendered into the top bar; scopes every widget's figures */}
          <TopbarPortal>
            <SegmentedPeriod value={period} onChange={setPeriod} options={HOME_PERIODS} />
          </TopbarPortal>

          {/* Row 1 — KPIs (full-width but shorter) */}
          <Box sx={kpiRowSx}>
            <OrdersWidget data={data} loading={loading.orders} onCreatedClick={handleCreatedClick} onDespatchedClick={handleDespatchedClick} />
            <UnitsWidget data={data} loading={loading.orders} onCreatedClick={handleCreatedClick} onDespatchedClick={handleDespatchedClick} />
            <ClosedReceiptsWidget data={data} loading={loading.receipts} onHeaderClick={handleClosedReceiptsClick} />
          </Box>

          {/* Row 2 — status / exceptions (adjacent so counts tally) / pending receipts */}
          <Box sx={rowSx}>
            <OrderStatusWidget data={data} loading={loading.orders} onStatusClick={handleStatusClick} />
            <ExceptionsWidget data={data} loading={loading.orders} onReasonClick={handleExceptionReasonClick} onAllExceptionsClick={handleAllExceptionsClick} />
            <PendingReceiptsWidget data={data} loading={loading.receipts} onHeaderClick={handlePendingReceiptsClick} onRowClick={handlePendingReceiptRowClick} />
          </Box>

          {/* Row 3 — slow movers / lowest stock / banner */}
          <Box sx={rowSx}>
            <SlowMovingWidget data={data} loading={loading.slowMoving} onRowClick={sku => handleProductClick(sku, "slow_moving_lines")} />
            <LowestStockWidget data={data} loading={loading.lowestStock} onRowClick={sku => handleProductClick(sku, "lowest_days_of_stock")} />
            <MarketingBanner />
          </Box>
        </Stack>
      </ContentWrapper>
    </MainComponent>
  )
})

export default Home
